/**
 * THE LINE-WEIGHT HIERARCHY for a PTA/PTB channel and its projected companions.
 *
 * <h2>Why this is its own module</h2>
 *
 * There are TWO renderers of PTA/PTB horizontal structure — the replay's `markArea`
 * (`timeBandOverlay`) and the cycle presets' `drawRect` overlays (`cycleBandGating`, drawn from
 * `App.renderCycleBands`) — reached by different buttons, labelled identically on screen, and
 * with nothing to tell a reader which one a given band came from. A weight declared in one of
 * them is a hierarchy the other does not have, and the two disagreeing is invisible until
 * somebody puts a replay and a cycle run on the same chart.
 *
 * <p>That is not hypothetical here: the projected range bands were built on one renderer and
 * reported twice as missing, because they were present on the other.
 *
 * <h2>The hierarchy itself</h2>
 *
 * <pre>
 *   ┌──────────────────┐   PROJECTED   no stroke — it was derived, not measured
 *   ╞══════════════════╡
 *   │   the CHANNEL    │   MEASURED    a stroke, and the thicker of the two
 *   ╞══════════════════╡
 *   └──────────────────┘   PROJECTED   no stroke
 * </pre>
 *
 * An outline is what says "this edge was measured". The projections have none, which is both the
 * distinction asked for and the reason the flanking weight is zero rather than thin: a hairline
 * would read as a measured edge drawn faintly.
 */

/**
 * The measured channel's stroke.
 *
 * <p>3, up from the 1.5 the cycle band shipped with — within the point or two asked for, and
 * enough that the middle band reads as the subject with two companions rather than as one of
 * three equal stripes. The replay channel had NO stroke at all, so for that renderer this is the
 * whole of the distinction.
 */
export const CHANNEL_BORDER_WIDTH = 3;

/**
 * The projections' stroke: none, which is their current weight and is deliberately unchanged.
 *
 * <p>Named rather than written as a bare 0 at two call sites, so "the outside bands keep their
 * weight" is a statement one edit can keep true of both renderers.
 */
export const PROJECTION_BORDER_WIDTH = 0;

/**
 * The channel's fill, a touch richer than its projections'.
 *
 * <p>The second half of the distinction, and deliberately SUBTLE: the three tiers were asked to
 * read as one cohesive structure, so this lifts the middle rather than fading the flanks. A
 * MULTIPLIER rather than a value, because the two renderers derive their fills differently — one
 * from a hex and an opacity, the other from an `rgba()` string — and what has to agree between
 * them is the relationship, not a number.
 */
export const CHANNEL_FILL_BOOST = 1.45;

/** The alpha of an `rgba(r, g, b, a)` string, or null when it is not one. */
export function alphaOf(rgba) {
  if (typeof rgba !== 'string') return null;
  const m = rgba.match(/^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)$/i);
  return m ? Number(m[1]) : null;
}

/**
 * The same colour with its alpha scaled, clamped at 1.
 *
 * <p>Returns the input UNTOUCHED when it is not an `rgba()` string — a hex, a named colour, or a
 * reader's configured hex from the workspace palette. That is the honest degradation: the
 * boost is a nicety and the stroke is what actually carries the distinction, so a configured
 * colour keeps the hierarchy without this having to guess at a format it was not given.
 *
 * @param {string} rgba an `rgba()` string
 * @param {number} factor how much to multiply the alpha by
 */
export function boostAlpha(rgba, factor = CHANNEL_FILL_BOOST) {
  const alpha = alphaOf(rgba);
  if (alpha === null || !Number.isFinite(factor)) return rgba;
  const next = Math.min(1, Math.round(alpha * factor * 1000) / 1000);
  return rgba.replace(/[\d.]+\s*\)$/, `${next})`);
}
